import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authService } from "../../services/auth.service";
import { REPO_ROLE_OPTIONS } from "../../constants/repoRoles";
import "../../styles/auth.css";
import "../../styles/users.css";

export default function AgentRegister() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    companyCode: searchParams.get("code") || "",
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "REPO_STAFF",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetOtpFlow = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.companyCode.trim() || !form.name.trim() || !form.email.trim()) {
      setError("Company code, name, and email are required.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const res = await authService.agentSelfRegister({
        ...form,
        companyCode: form.companyCode.trim().toUpperCase(),
        name: form.name.trim(),
        email: form.email.trim(),
      });
      const data = res?.data || {};
      setRegistrationId(data.registrationId || "");
      setOtpSent(Boolean(res?.requiresOtp));
      setResendCooldown(Number(data.resendAvailableInSeconds || 60));
      if (!res?.requiresOtp) {
        setSuccess(res?.message || "Registration submitted.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP received by SMS.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await authService.verifyAgentRegistration(registrationId, otp);
      setOtpSent(false);
      setSuccess(res?.message || "Registration successful. Await admin activation.");
      setOtp("");
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await authService.resendAgentRegistrationOtp(registrationId);
      setResendCooldown(Number(res?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP.");
      const match = String(err?.response?.data?.message || "").match(/(\d+)\s*seconds/);
      if (match) setResendCooldown(Number(match[1]));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: 520, width: "100%" }}>
        <h2>Agent self-registration</h2>
        <p className="muted">
          Register with your company code from the repo admin. Your account stays
          inactive until the admin activates it.
        </p>

        {!otpSent ? <form onSubmit={handleSubmit} className="create-company-form" style={{ marginTop: 16 }}>
          <div className="form-group">
            <label>Company code *</label>
            <input
              name="companyCode"
              value={form.companyCode}
              onChange={handleChange}
              placeholder="e.g. HIM1114"
              required
            />
          </div>
          <div className="form-group">
            <label>Full name *</label>
            <input name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Mobile *</label>
            <input type="tel" name="phone" value={form.phone} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input type="password" name="password" value={form.password} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Role</label>
            <select name="role" value={form.role} onChange={handleChange}>
              <option value="REPO_STAFF">Repo Staff</option>
              {REPO_ROLE_OPTIONS.filter((r) => r.value !== "REPO_STAFF").map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="error-text">{error}</p>}
          {success && <p className="cfm-status">{success}</p>}

          <div className="create-company-actions">
            <Link to="/repo-agent/login" className="secondary-page-btn">
              Back to login
            </Link>
            <button type="submit" className="primary-page-btn" disabled={loading}>
              {loading ? "Submitting…" : "Register"}
            </button>
          </div>
        </form> : (
          <form onSubmit={handleVerifyOtp} className="create-company-form agent-register-otp" style={{ marginTop: 16 }}>
            <h3>Verify Mobile Number</h3>
            <p className="muted">
              We sent a 6-digit verification OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.
            </p>
            <div className="form-group">
              <label htmlFor="agent-register-otp">Enter OTP</label>
              <input
                id="agent-register-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit OTP"
                required
              />
            </div>
            {resendCooldown > 0 ? <p className="muted">Resend available in {resendCooldown}s</p> : null}
            {error && <p className="error-text">{error}</p>}
            <div className="create-company-actions">
              <button type="button" className="secondary-page-btn" onClick={resetOtpFlow} disabled={loading}>
                Back / Edit
              </button>
              <button type="button" className="secondary-page-btn" onClick={handleResendOtp} disabled={loading || resendCooldown > 0}>
                Resend OTP
              </button>
              <button type="submit" className="primary-page-btn" disabled={loading || otp.length !== 6}>
                {loading ? "Verifying…" : "Verify OTP"}
              </button>
            </div>
          </form>
        )}
        {success && <p className="cfm-status">{success}</p>}
      </div>
    </div>
  );
}
